import React from 'react';

import {
    isCommunitiesFeatureEnabled,
    isSpaceCreationInFrontOfficeFeatureEnabled,
    selectedCommunityIdSelector,
} from '@lumapps/communities/ducks/selectors';
import { Community } from '@lumapps/communities/types';
import { CommunityPickerDialog } from '@lumapps/community-pickers/components/CommunityPickerDialog';
import { useSearchForPostingPicker } from '@lumapps/community-pickers/hooks/useSearchForPostingPicker';
import { EventNotificationDialog } from '@lumapps/events/components/EventNotificationDialog';
import { isEventNotificationDialogOpenSelector } from '@lumapps/events/ducks/selectors';
import { useEventDialog } from '@lumapps/events/hooks/useEventDialog';
import { PostDialog } from '@lumapps/posts/components/PostDialog';
import { editedPostSelector, isQuickPostDisabled } from '@lumapps/posts/ducks/selectors';
import { usePostDialog } from '@lumapps/posts/hooks/usePostDialog';
import { useSelector } from '@lumapps/redux/react';
import { routerApi } from '@lumapps/router/routers';
import { SpaceCreationDialog } from '@lumapps/spaces-creation-dialog/components/SpaceCreationDialog';
import { spaceView } from '@lumapps/spaces/routes';
import { type SpaceLegacyData, SpaceViews } from '@lumapps/spaces/types';
import { useTranslate } from '@lumapps/translations';
import { GLOBAL } from '@lumapps/translations/keys';
import { useBooleanState } from '@lumapps/utils/hooks/useBooleanState';

/** Hook grouping all community contributions that can be triggered in the contribution menu. */
export const useCommunityContributionMenu = ({ triggerElement }: { triggerElement: React.RefObject<any> }) => {
    const { translateKey, translateObject } = useTranslate();
    const [isSpaceCreationDialogOpen, setIsSpaceCreationDialogOpen] = React.useState<boolean>(false);
    const areCommunitiesEnabled = useSelector(isCommunitiesFeatureEnabled);
    const isQPostDisabled = useSelector(isQuickPostDisabled);
    const isCreateNewPostEnabled = areCommunitiesEnabled && !isQPostDisabled;
    const editedPost = useSelector(editedPostSelector);
    const selectedCommunityId = useSelector(selectedCommunityIdSelector);
    const isSpaceCreationInFrontOfficeEnabled = useSelector(isSpaceCreationInFrontOfficeFeatureEnabled);

    const { openEventDialog } = useEventDialog();
    const {
        isOpen: isPostDialogOpen,
        createPost: openPostDialog,
        closePostDialog,
    } = usePostDialog({ openEventDialog });
    const isEventNotificationDialogOpen = useSelector(isEventNotificationDialogOpenSelector);

    const [isCommunityPickerOpen, toggleCommunityPicker, closeCommunityPicker, openCommunityPicker] =
        useBooleanState(false);
    const handleCommunityConfirmed = React.useCallback(
        (communities: Community[]) => {
            const { uid, renderingType } = communities[0] as Community;
            openPostDialog({ containerId: uid, renderingType });

            closeCommunityPicker();
        },
        [closeCommunityPicker, openPostDialog],
    );

    const communityContribDialogs = isCreateNewPostEnabled ? (
        <>
            <CommunityPickerDialog
                communityPickerHook={useSearchForPostingPicker}
                onConfirm={handleCommunityConfirmed}
                onCancel={toggleCommunityPicker}
                isOpen={isCommunityPickerOpen}
                parentElement={triggerElement}
                confirmButtonProps={{ children: translateKey(GLOBAL.NEXT) }}
            />
            <PostDialog
                isOpen={isPostDialogOpen}
                communityId={selectedCommunityId}
                post={editedPost}
                triggerElement={triggerElement}
                onClose={closePostDialog}
            />
        </>
    ) : null;

    const eventNotificationDialog = (
        <EventNotificationDialog isOpen={isEventNotificationDialogOpen} triggerElement={triggerElement} />
    );

    const onCreateNewSpace = () => setIsSpaceCreationDialogOpen(true);

    const onSaveNewSpace = (shouldRedirect: boolean, space?: SpaceLegacyData) => {
        setIsSpaceCreationDialogOpen(false);

        if (shouldRedirect && space) {
            const spaceRoute = spaceView({
                params: { id: space.id, slug: translateObject(space.slug) as string, view: SpaceViews.home },
            });
            routerApi.redirect(spaceRoute);
        }
    };

    const spaceCreationDialog = isSpaceCreationInFrontOfficeEnabled ? (
        <SpaceCreationDialog isOpen={isSpaceCreationDialogOpen} onClose={onSaveNewSpace} />
    ) : null;

    return {
        communityContribDialogs,
        onCreatePost: isCreateNewPostEnabled ? openCommunityPicker : undefined,
        onCreateNewSpace: isSpaceCreationInFrontOfficeEnabled ? onCreateNewSpace : undefined,
        spaceCreationDialog,
        eventNotificationDialog,
    };
};
